import { createSlice } from '@reduxjs/toolkit';
import { resetAuthorizationToken, setAuthorizationToken } from '../api';
import { ApiStatus, AuthState } from '../types';
import { LoginFormInterface } from '../Pages/Login/LoginNew';
import { toast } from 'react-toastify';
import store from './store';
import { generateRandomString } from '../Utils/helperFns';
import {
  apiSuccessCode,
  getApiCall,
  postApiCall,
  postApiCallWithOutToken,
  putApiCall,
} from '../api/methods';
import { AxiosResponse } from 'axios';
import endPoints from '../api/endpoint';
import { setCommonDataLoader } from './commonSlice';
import { logout as chatLogout } from '../Pages/Chats/chat.slice';

const initialState: AuthState = {
  status: false,
  userData: null,
  token: null,
  apiStatus: ApiStatus.idle,
  userlist: {},
  isNewUser: false,
};
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action) => {
      const {
        payload: { userData, token, isNewUser, isUserDetailsAdded },
      } = action;

      state.status = true;
      state.userData = userData;
      state.token = token;
      state.isNewUser = isNewUser;
      state.isUserDetailsAdded = isUserDetailsAdded;
      setAuthorizationToken(token);
      // localStorage.setItem('token', token)
    },
    logout: state => {
      state.status = false;
      state.userData = null;
      state.token = null;

      // Preserve tokenFCM while clearing everything else
      const tokenFCM = localStorage.getItem('tokenFCM');

      // Clear all localStorage items
      localStorage.clear();

      // Restore tokenFCM if it existed
      if (tokenFCM) {
        localStorage.setItem('tokenFCM', tokenFCM);
        console.log('🔑 Preserved tokenFCM during logout');
      }

      localStorage.setItem('d', '1');
      resetAuthorizationToken();
      // Disconnect socket on logout using global function
      try {
        if ((window as any).disconnectSocket) {
          (window as any).disconnectSocket();
          console.log('🔌 Socket disconnected on logout');
        }
      } catch (error) {
        console.log('Socket disconnect error:', error);
      }
    },
    updateApiStatus: (state, action) => {
      const { payload } = action;
      state.apiStatus = payload;
    },

    updateUserDetails: (state, action) => {
      // console.log('Update PRR =>', action)
      const {
        payload: { userData, token },
      } = action;
      state.userData = { ...state.userData, ...action.payload };
    },
    updateStep: (state, action) => {
      if (state.userData == null) {
        state.userData = { formNextStep: action.payload.step };
      } else {
        state.userData['formNextStep'] = action.payload.step;
      }
    },
    saveUserData: (state, action) => {
      if (state.userData == null) {
        state.userData = { [action.payload.key]: action.payload.value };
      } else {
        state.userData[action.payload.key] = action.payload.value;
      }
    },
    setUserProfile: (state, action) => {
      const { payload }: any = action;
      state.userlist = payload;
    },
  },
});
export const {
  login,
  logout,
  updateStep,
  saveUserData,
  updateUserDetails,
  updateApiStatus,
  setUserProfile,
} = authSlice.actions;

// export const loginCall = (
//   data: LoginFormInterface,
//   cb?: any,
//   errorCb?: any,
// ) => {
//   // toast.dismiss()
//   const {
//     phoneNo,
//     countryAbbreviation,
//     countryCode: { dial_code, code },
//   }: any = data;
//   const { dispatch } = store;

//   dispatch(updateApiStatus(ApiStatus.loading)); // Update API status to loading

//   const payload = {
//     phoneNo,
//     countryAbbreviation: code,
//     countryCode: dial_code,
//     deviceName: `${generateRandomString(5)}_${
//       localStorage.getItem('email') || ' '
//     }`,
//     type: 'LOGIN',
//     browser: navigator.userAgent,
//   };

//   postApiCallWithOutToken(
//     endPoints.login,
//     payload,
//     (s: AxiosResponse) => {
//       const {
//         data: { message },
//       } = s;

//       console.log('login', message);
//       cb(message);

//       dispatch(
//         login({
//           status: true,
//           userData: { ...payload },
//           token: data?.accessToken, // Access token from API response
//         }),
//       );

//       // Save payload in Redux

//       dispatch(updateApiStatus(ApiStatus.succeeded)); // Update API status to succeeded
//     },
//     (e: any) => {
//       toast.error(e.data.message);
//       // cb(e.data.message)
//       errorCb();
//       dispatch(updateApiStatus(ApiStatus.failed)); // Update API status to failed
//     },
//   );
// };

export const loginCall = (data: LoginFormInterface, navigate: any) => {
  toast.dismiss();
  const {
    phoneNo,
    countryAbbreviation,
    countryCode: { dial_code, code },
  }: any = data;
  const { dispatch } = store;

  dispatch(updateApiStatus(ApiStatus.loading)); // Update API status to loading

  const payload = {
    phoneNo,
    countryAbbreviation: code,
    countryCode: dial_code,
    deviceName: `${generateRandomString(5)}_${
      localStorage.getItem('email') || ' '
    }`,
    type: 'LOGIN',
    browser: navigator.userAgent,
  };

  postApiCallWithOutToken(
    endPoints.login,
    payload,
    (s: AxiosResponse) => {
      const {
        data: { data },
      } = s;

      // Save payload in Redux
      dispatch(
        login({
          status: true,
          userData: { ...payload },
          token: data?.accessToken, // Access token from API response
        }),
      );

      // Navigate to verify page
      navigate('/verify');

      dispatch(updateApiStatus(ApiStatus.succeeded)); // Update API status to succeeded
    },
    (e: any) => {
      toast.error(e.data.message);
      dispatch(updateApiStatus(ApiStatus.failed)); // Update API status to failed
    },
  );
};

export const registerCall = (data: any, cb: () => void) => {
  // toast.dismiss()
  const { dispatch } = store;

  dispatch(updateApiStatus(ApiStatus.loading));
  delete data['termsAndConditions']; // Removing termsAndConditions from payload

  // Ensure deviceToken and deviceId are set
  const deviceToken =
    localStorage.getItem('tokenFCM') || generateRandomString(5);
  const email = localStorage.getItem('email') || '';

  data['deviceToken'] = deviceToken;
  data['deviceId'] = generateRandomString(10); // Generate deviceId as a random string
  data['deviceName'] = `${generateRandomString(5)}_${email}`; // Generate deviceName using random string and email

  postApiCall(
    endPoints.register,
    data,
    (s: any) => {
      const {
        data: { statusCode },
      } = s;
      if (data && statusCode && statusCode === apiSuccessCode.created) {
        localStorage.setItem('email', data['email']);
        cb(data);
      }
      dispatch(
        login({
          // status: true,
          userData: { ...data },
          // token: data?.accessToken, // Access token from API response
        }),
      );
      dispatch(updateApiStatus(ApiStatus.succeeded));
    },
    (e: any) => {
      toast.error(e.data.message);
    },
  );
};

export const getUserList = (cb?: any) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));
  const apiendpoint = endPoints.production.userProfile;
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data: data } = s;
      if (data.data) {
        dispatch(setUserProfile(data.data));
        cb(data.data);
      }
      dispatch(setCommonDataLoader(false));
    },

    (e: any) => {},
  );
};

export const logOutMethod = (values?: any, cb?: any) => {
  const { dispatch } = store;
  postApiCall(
    endPoints.logout,
    values,
    (s: any) => {
      const {
        data: { statusCode },
      } = s;

      if (statusCode && statusCode === 200) {
        // notify(message, 'success')
        // Disconnect socket before logout
        try {
          if ((window as any).disconnectSocket) {
            (window as any).disconnectSocket();
            console.log('🔌 Socket disconnected on API logout');
          }
        } catch (error) {
          console.log('Socket disconnect error:', error);
        }
        dispatch(logout());
        dispatch(chatLogout()); // Dispatch chat logout action
        if (cb) cb();
      }
    },
    (e: any) => {
      // if (e?.data && e?.data.message) {
      //   notify(e?.data.message, 'error')
      // }
    },
  );
};

export default authSlice.reducer;
